import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PUBLIC_ROUTES } from '../../constants/routeConstants';
import { VALIDATION_MESSAGES } from '../../utils/validationMessages';
import PasswordInput from '../../components/common/PasswordInput';
import { Button } from '../../components/ui/button';

import API_ENDPOINTS from '../../config/api';
import axios from 'axios';
import LoaderOverlay from '../../components/common/LoaderOverlay';

interface ResetPasswordData {
  newPassword: string;
  confirmPassword: string;
}

interface FormErrors {
  newPassword?: string;
  confirmPassword?: string;
}

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ResetPasswordData>({
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [tokenChecking, setTokenChecking] = useState(true);
  const [uiOnlyMode] = useState(false);

  const isFormValid =
    formData.newPassword.trim() !== '' &&
    formData.confirmPassword.trim() !== '' &&
    formData.newPassword.length >= 8 &&
    formData.newPassword.length <= 20 &&
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
      formData.newPassword
    ) &&
    formData.newPassword === formData.confirmPassword;

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setTokenChecking(false);
      return;
    }

    const validateToken = async () => {
      try {
        if (uiOnlyMode) {
          if (token.length > 10) {
            setTokenValid(true);
          } else {
            setTokenValid(false);
          }
        } else {
          if (token.length > 20) {
            setTokenValid(true);
          } else {
            setTokenValid(false);
          }
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setTokenValid(false);
      } finally {
        setTokenChecking(false);
      }
    };

    validateToken();
  }, [token, uiOnlyMode]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = VALIDATION_MESSAGES.password.required;
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = VALIDATION_MESSAGES.password.minLength;
    } else if (formData.newPassword.length > 20) {
      newErrors.newPassword = VALIDATION_MESSAGES.password.maxLength;
    } else if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
        formData.newPassword
      )
    ) {
      newErrors.newPassword = VALIDATION_MESSAGES.password.weak;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = VALIDATION_MESSAGES.confirmPassword.required;
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = VALIDATION_MESSAGES.confirmPassword.mismatch;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNewPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      newPassword: value,
    }));

    const newErrors: FormErrors = { ...errors };

    if (!value) {
      newErrors.newPassword = VALIDATION_MESSAGES.password.required;
    } else if (value.length < 8) {
      newErrors.newPassword = VALIDATION_MESSAGES.password.minLength;
    } else if (value.length > 20) {
      newErrors.newPassword = VALIDATION_MESSAGES.password.maxLength;
    } else if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
        value
      )
    ) {
      newErrors.newPassword = VALIDATION_MESSAGES.password.weak;
    } else {
      newErrors.newPassword = undefined;
    }

    if (formData.confirmPassword) {
      if (value !== formData.confirmPassword) {
        newErrors.confirmPassword =
          VALIDATION_MESSAGES.confirmPassword.mismatch;
      } else {
        newErrors.confirmPassword = undefined;
      }
    }

    setErrors(newErrors);
  };

  const handleConfirmPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      confirmPassword: value,
    }));

    const newErrors: FormErrors = { ...errors };

    if (!value) {
      newErrors.confirmPassword = VALIDATION_MESSAGES.confirmPassword.required;
    } else if (formData.newPassword !== value) {
      newErrors.confirmPassword = VALIDATION_MESSAGES.confirmPassword.mismatch;
    } else {
      newErrors.confirmPassword = undefined;
    }

    setErrors(newErrors);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!token) {
      toast.error('Invalid reset token');
      return;
    }

    setLoading(true);

    try {
      if (uiOnlyMode) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        toast.success('Password reset successful! (UI Mode)');

        setTimeout(() => {
          navigate(PUBLIC_ROUTES.LOGIN);
        }, 2000);
      } else {
        await axios.post(
          API_ENDPOINTS.AUTH.RESET_PASSWORD,
          {
            token,
            newPassword: formData.newPassword,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        toast.success('Password reset successful!');

        setTimeout(() => {
          navigate(PUBLIC_ROUTES.LOGIN);
        }, 2000);
      }
    } catch (error: any) {
      console.error('Reset password error:', error);

      if (!uiOnlyMode) {
        let errorMessage = 'Password reset failed';

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.data?.message) {
          errorMessage = error.data.message;
        } else if (error.data?.error) {
          errorMessage = error.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        }

        toast.error(errorMessage);
      } else {
        toast.error('Password reset failed (UI Mode)');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(PUBLIC_ROUTES.LOGIN);
  };

  if (tokenChecking) {
    return (
      <LoaderOverlay isLoading={true} loadingText="Validating reset token...">
        <div className="min-h-screen" />
      </LoaderOverlay>
    );
  }

  if (!tokenValid || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white py-8 px-6 shadow rounded-lg text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="h-16 w-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Invalid Reset Link
          </h2>
          <p className="text-gray-600 mb-6">
            This password reset link is invalid or has expired. Please request a
            new password reset.
          </p>
          <Button
            onClick={() => navigate(PUBLIC_ROUTES.FORGOT_PASSWORD)}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-semibold text-white bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            Request New Reset Link
          </Button>
        </div>
      </div>
    );
  }

  return (
    <LoaderOverlay isLoading={loading} loadingText="Resetting password...">
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Reset Your Password
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Please enter your new password below
            </p>
          </div>

          <div className="bg-white py-8 px-6 shadow rounded-lg">
            <form
              onSubmit={handleSubmit}
              noValidate
              className="space-y-6 text-left"
            >
              <div>
                <PasswordInput
                  id="newPassword"
                  name="newPassword"
                  label="New Password"
                  value={formData.newPassword}
                  onChange={handleNewPasswordChange}
                  error={errors.newPassword}
                  showToggle={true}
                  autoComplete="new-password"
                  placeholder="Enter new password"
                  required
                />
              </div>

              <div>
                <PasswordInput
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  error={errors.confirmPassword}
                  showToggle={true}
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <div className="flex space-x-4">
                <Button
                  onClick={handleCancel}
                  className="flex-1 flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-base font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  className="flex-1 flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-semibold text-white bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:bg-blue-600 transition-colors duration-200"
                  disabled={loading || !isFormValid}
                >
                  Submit
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </LoaderOverlay>
  );
};

export default ResetPassword;
